import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Clock, Plane, AlertCircle } from 'lucide-react';
import { calculatePriority } from '@/utils/gameLogic';

export const CustomerCard = ({ customer, currentTime, currentDay = 1, onClick, isSelected }) => {
  const priority = calculatePriority(customer, currentTime, currentDay);
  const { displayColor, minutesUntilDeparture, urgencyLevel } = priority;
  
  // Calculate progress bar percentage and color
  // Use fixed maximum of 10 hours (600 minutes) as scale
  const maxTimeUntilDeparture = 10 * 60; // 10 hours in minutes
  
  // Calculate percentage (0-100) - how much time is left relative to max 10 hours
  // If more than 10 hours remain, show 100%
  const progressPercentage = Math.max(0, Math.min(100, (minutesUntilDeparture / maxTimeUntilDeparture) * 100));
  
  // Countdown updates based on currentTime prop changes
  // No need for separate timer - it updates when currentTime changes from parent
  
  const priorityClasses = {
    critical: 'priority-critical border-[hsl(var(--critical))]',
    high: 'priority-high border-[hsl(var(--high))]',
    normal: 'priority-normal border-[hsl(var(--normal))]'
  };
  
  const badgeColors = {
    critical: 'bg-[hsl(var(--critical))]/20 text-[hsl(var(--critical))] border-[hsl(var(--critical))]/50',
    high: 'bg-[hsl(var(--high))]/20 text-[hsl(var(--high))] border-[hsl(var(--high))]/50',
    normal: 'bg-[hsl(var(--normal))]/20 text-[hsl(var(--normal))] border-[hsl(var(--normal))]/50'
  };
  
  const textColors = {
    critical: 'text-[hsl(var(--critical))]',
    high: 'text-[hsl(var(--high))]',
    normal: 'text-[hsl(var(--normal))]'
  };
  
  // Tier color mapping
  const tierColors = {
    VIP: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
    Business: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
    Standard: 'bg-gray-500/20 text-gray-400 border-gray-500/50'
  };
  
  const tierColor = tierColors[customer.profile.tier] || tierColors.Standard;
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      className={`relative rounded-lg p-3 cursor-pointer group transition-all duration-200 ${
        priorityClasses[displayColor]
      } ${
        isSelected ? 'ring-2 ring-[hsl(var(--primary))]' : ''
      } hover:shadow-lg`}
      onClick={onClick}
    >
      {/* Priority Badge - moved to top of content flow */}
      <div className="flex items-center justify-between mb-2">
        <Badge 
          className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${
            badgeColors[displayColor]
          }`}
        >
          {urgencyLevel === 'CRITICAL' ? 'CRIT' : urgencyLevel === 'STANDARD' ? 'STD' : urgencyLevel}
        </Badge>
        <Badge className={`text-[10px] px-1.5 py-0 border ${tierColor}`}>
          {customer.profile.tier}
        </Badge>
      </div>
      
      {/* Customer Info */}
      <div className="flex items-start gap-3 mb-2">
        <div className="relative flex-shrink-0">
          <Avatar className={`w-10 h-10 ring-2 ring-[hsl(var(--${displayColor}))]`}>
            <AvatarImage src={customer.profile.avatar} alt={customer.profile.name} />
            <AvatarFallback>{customer.profile.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
          </Avatar>
          <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full animate-pulse-slow ${
            displayColor === 'critical' ? 'bg-[hsl(var(--critical))]' :
            displayColor === 'high' ? 'bg-[hsl(var(--high))]' :
            'bg-[hsl(var(--normal))]'
          }`} />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-foreground truncate">
            {customer.profile.name}
          </h3>
          <p className="text-xs text-muted-foreground truncate">
            {customer.profile.title}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {customer.profile.company}
          </p>
        </div>
      </div>
      
      {/* Route */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
        <span className="font-mono font-semibold text-[11px]">{customer.trip.from}</span>
        <Plane className="w-3 h-3" />
        <span className="font-mono font-semibold text-[11px]">{customer.trip.to}</span>
      </div>
      
      {/* Countdown Timer */}
      <div className="flex items-center gap-1.5 mb-2">
        <Clock className="w-3.5 h-3.5" />
        <span className={`font-mono text-xs font-bold animate-countdown ${
          textColors[displayColor]
        }`}>
          {Math.floor(minutesUntilDeparture / 60)}h {minutesUntilDeparture % 60}m
        </span>
        <span className="text-[10px] text-muted-foreground">to departure</span>
      </div>
      
      {/* Time Progress Bar */}
      <div className="w-full relative h-1.5 bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full transition-all duration-300 bg-[hsl(var(--${displayColor}))]`}
          style={{ width: `${progressPercentage}%` }}
        />
      </div>
    </motion.div>
  );
};